import { cn } from "@/lib/utils";
import { NvCardLink } from "./nv-card-link";
import { NvCheckIcon, NvLockIcon, NvStarIcon } from "./nv-icons";
import { NvPill } from "./nv-pill";

// HOME list rows: Figma "Row / Certified Agent" (185:4451) and "Row / Rank" (185:4461).

export interface NvCertifiedAgentRowProps {
  name: string;
  supplier: string;
  summary: string;
  logoUrl?: string;
  rating?: number;
  adoptionCount?: number;
  // Guest · 승인 대기 users see a lock notice instead of rating and adoption count (203:13746).
  locked?: boolean;
  to?: string;
  linkState?: unknown;
  className?: string;
}

function NvCertifiedAgentRow({
  name,
  supplier,
  summary,
  logoUrl,
  rating,
  adoptionCount,
  locked = false,
  to,
  linkState,
  className,
}: NvCertifiedAgentRowProps) {
  const adoption = adoptionCount !== undefined ? `도입 ${adoptionCount.toLocaleString("ko-KR")}건` : undefined;
  const meta = locked ? (
    <>
      <NvLockIcon size={13} />
      상세는 이용 권한 필요
    </>
  ) : (
    <>
      {rating !== undefined && <NvStarIcon size={13} />}
      {[rating !== undefined && rating.toFixed(1), adoption].filter(Boolean).join(" · ")}
    </>
  );
  const hasMeta = locked || rating !== undefined || adoption !== undefined;

  return (
    <div className={cn("relative flex items-center gap-[14px] border-b border-nv-border py-[11.5px]", className)}>
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-nv-control bg-nv-bg-page">
        {logoUrl && <img src={logoUrl} alt="" className="h-full w-full object-contain" />}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <NvCardLink to={to} state={linkState} className="truncate text-[14px] font-medium leading-[17px] text-nv-text-primary">
          {name}
        </NvCardLink>
        <span className="truncate text-[11px] leading-[13px] text-nv-text-secondary">{supplier}</span>
        {/* △는 한 줄 소개를 흐리게 (Figma 203:13745 LAYER_BLUR 10) */}
        <span className={cn("truncate text-[12px] leading-[14px] text-nv-text-secondary", locked && "select-none blur-[5px]")} aria-hidden={locked || undefined}>
          {summary}
        </span>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-[6px]">
        <NvPill tone="success" className="gap-1">
          <NvCheckIcon size={13} />
          Certified
        </NvPill>
        {hasMeta && <span className="inline-flex items-center gap-1 text-[11px] leading-[13px] text-nv-text-secondary">{meta}</span>}
      </div>
    </div>
  );
}

// Positive = moved up, negative = moved down, 0 = no change.
export type NvRankChange = number | "new";

function rankChangeView(change: NvRankChange) {
  if (change === "new") return { label: "NEW", className: "text-nv-text-primary" };
  if (change > 0) return { label: `▲${change}`, className: "text-nv-success" };
  if (change < 0) return { label: `▼${Math.abs(change)}`, className: "text-nv-danger" };
  return { label: "—", className: "text-nv-text-secondary" };
}

export interface NvRankRowProps {
  rank: number;
  name: string;
  summary: string;
  change?: NvRankChange;
  to?: string;
  linkState?: unknown;
  className?: string;
}

function NvRankRow({ rank, name, summary, change, to, linkState, className }: NvRankRowProps) {
  const changeView = change === undefined ? null : rankChangeView(change);

  return (
    <div className={cn("relative flex items-center gap-[14px] border-b border-nv-border py-3", className)}>
      <span className="w-5 shrink-0 font-num text-[13px] leading-4 text-nv-text-secondary">{rank}</span>

      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <NvCardLink to={to} state={linkState} className="truncate text-[14px] font-medium leading-[17px] text-nv-text-primary">
          {name}
        </NvCardLink>
        <span className="truncate text-[11px] leading-[13px] text-nv-text-secondary">{summary}</span>
      </div>

      {changeView && (
        <span className={cn("shrink-0 font-num text-[12px] font-bold leading-[15px]", changeView.className)}>{changeView.label}</span>
      )}
    </div>
  );
}

export { NvCertifiedAgentRow, NvRankRow };
